import { PayloadAction } from '@reduxjs/toolkit';
import { EventChannel, eventChannel } from 'redux-saga';
import { all, call, fork, put, select, take, takeEvery } from 'redux-saga/effects';
import { RootState } from 'types';
import { EntityTypes } from 'constants/entities';
import { IUpdateState, updateState } from 'core/actions/product/state';
import { selectAction } from 'core/selectors/product/action/action';
import { selectScene } from 'core/selectors/product/scene/scene';
import { selectSceneAsset } from 'core/selectors/product/scene/sceneAsset';
import { selectSceneConfig, selectVoiceSceneConfig } from 'core/selectors/product/scene/sceneConfig';
import { IViewer, updateVideoState, updateViewerState, ViewerState } from 'core/slices/player';
import { IActionBasic } from 'types/domain/action';
import { AssetType, IAsset } from 'types/app/asset';
import { ISceneConfig, ISceneDAO, SceneType } from 'types/domain/scene';
import { backgroundVideo, IBackgroundClip, IBackgroundState } from 'utils/backgroundVideo';

/**
 * The scene video behind a voiceover plays on its own muted element (see
 * utils/backgroundVideo). It is wanted only when the current action has a voiceover
 * *and* sits on a video scene; otherwise the scene video, if any, stays on #videoEl.
 * Moving between those two cases reattaches the scene video on the other element.
 */
function* currentClip() {
  const action: IActionBasic = yield select(state => selectAction(state, undefined));
  const scene: ISceneDAO = yield select(state => selectScene(state, undefined));
  // The SCENE and ACTION pointers update separately; wait until they agree.
  if (!action?.voiceSceneId || !scene || scene.type !== SceneType.VIDEO || action.sceneId !== scene.id) return null;

  const asset: IAsset = yield select(state => selectSceneAsset(state, undefined));
  if (!asset || asset.type !== AssetType.VIDEO || !(asset.url || asset.fallback)) return null;

  const config: ISceneConfig = yield select(state => selectSceneConfig(state, undefined, undefined));
  const voiceConfig: ISceneConfig = yield select(state => selectVoiceSceneConfig(state, undefined, undefined));

  const clip: IBackgroundClip = {
    assetId: asset.id,
    url: asset.url || asset.fallback,
    start: config?.start || 0,
    end: config?.end || null,
    loop: !!config?.loop,
    // startVideo's default, so a replay of the voiceover is recognised.
    leadStart: voiceConfig?.start || 0.1,
  };
  return clip;
}

export function* syncBackgroundVideo() {
  const clip: IBackgroundClip | null = yield call(currentClip);
  if (clip) {
    yield call([backgroundVideo, backgroundVideo.show], clip);
    return;
  }
  if (!backgroundVideo.assetId) return;

  // Like #videoEl, hold the last frame through the slide transition and only
  // detach once the viewer settles (viewerRestSaga below).
  const viewerState: ViewerState = yield select((state: RootState) => state.core.player.state.viewer.state);
  if (viewerState === ViewerState.RESTING) yield call([backgroundVideo, backgroundVideo.stop]);
  else yield call([backgroundVideo, backgroundVideo.pause]);
}

export function* removeBackgroundVideo() {
  yield call([backgroundVideo, backgroundVideo.stop]);
}

function* updateStateSaga(action: PayloadAction<Partial<IUpdateState>>) {
  if (action.payload.type !== EntityTypes.ACTION && action.payload.type !== EntityTypes.SCENE) return;
  yield call(syncBackgroundVideo);
}

function* viewerRestSaga(action: PayloadAction<IViewer>) {
  if (action.payload.state !== ViewerState.RESTING || !backgroundVideo.assetId) return;
  const clip: IBackgroundClip | null = yield call(currentClip);
  if (!clip) yield call([backgroundVideo, backgroundVideo.stop]);
}

const backgroundStateChannel = (): EventChannel<IBackgroundState> =>
  eventChannel(emit => backgroundVideo.subscribe(emit));

function* reportState() {
  const channel: EventChannel<IBackgroundState> = yield call(backgroundStateChannel);
  try {
    while (true) {
      const { assetId, ready }: IBackgroundState = yield take(channel);
      yield put(updateVideoState({ bgAssetId: assetId, bgReady: ready }));
    }
  } finally {
    channel.close();
  }
}

export function* backgroundVideoSaga() {
  yield all([
    fork(reportState),
    fork(function* () {
      yield takeEvery(updateState.type, updateStateSaga);
    }),
    fork(function* () {
      yield takeEvery(updateViewerState.type, viewerRestSaga);
    }),
  ]);
}
